import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  getCharacters,
  getBulkCharacterDeaths,
  getBulkCharacterVoices,
  getBulkIsAlive,
  getBulkLastInventoryUpdate,
  deleteCharacter as deleteCharacterService,
  bulkDeleteCharacters as bulkDeleteCharactersService,
} from '../../services/characterService';
import type { RootState } from '../store';

// Define the Character type based on backend entity and needs
export interface Character {
  id: string;
  name: string;
  level: number;
  experience: string;
  game: string; // 'PoE1' | 'PoE2'
  league: string;
  divineOrbCount?: number; // Count of Divine Orbs for the character
  voicesCount?: number; // Count of "Voices" for the character
  deathsCount?: number;
  deathsPerHour?: number;
  state?: string; // Character state (e.g., 'Active', 'Idle', etc.)
  hostname?: string; // Hostname where the character is running
  note?: string; // User note for the character
  boostingState?: number; // Boosting state: 0 = Active, 1 = Boosted by Operator, 2 = Given to external Booster
  boostingStateChangedAt?: string; // Timestamp when boosting state was last changed
  gold?: string | null; // In-game gold currency amount
  atlasPoints?: number | null;
  build?: string | null; // Character build name
  stage?: string | null; // Farming stage/strategy
  gameAccount?: {
    id: string;
    name: string;
  }; // Game account information
}

interface CharactersState {
  characters: Character[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null | undefined;
  aliveStatuses: Record<string, string | null>;
  aliveStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  lastInventoryUpdateTimestamps: Record<string, string | null>;
  lastInventoryUpdateStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: CharactersState = {
  characters: [],
  status: 'idle',
  error: null,
  aliveStatuses: {},
  aliveStatus: 'idle',
  lastInventoryUpdateTimestamps: {},
  lastInventoryUpdateStatus: 'idle',
};

// Async thunk to fetch characters
export const fetchCharacters = createAsyncThunk<
  Character[], // Return type
  void, // Argument type (void for no argument)
  { rejectValue: string; state: RootState } // ThunkAPI config
>('characters/fetchCharacters', async (_, thunkAPI) => {
  try {
    // Get selected game and league from filter state
    const state = thunkAPI.getState();
    const selectedGame = state.filter.selectedGame;
    const selectedLeague = state.filter.selectedLeague;

    // Pass game and league to the API call
    // This will also fetch Divine Orb counts from the service
    const response = await getCharacters(selectedGame, selectedLeague);
    return response;
  } catch (error: any) {
    let errorMessage = 'Failed to fetch characters';
    if (error.response && error.response.data && error.response.data.message) {
      if (Array.isArray(error.response.data.message)) {
        errorMessage = error.response.data.message.join(', ');
      } else {
        errorMessage = error.response.data.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    return thunkAPI.rejectWithValue(errorMessage);
  }
});

// Async thunk to fetch deaths count for multiple characters
export const fetchCharacterDeaths = createAsyncThunk<
  { [key: string]: number },
  { characterIds: string[]; hours: number },
  { rejectValue: string }
>('characters/fetchCharacterDeaths', async ({ characterIds, hours }, thunkAPI) => {
  try {
    const deaths = await getBulkCharacterDeaths(characterIds, hours);
    return deaths;
  } catch (error: any) {
    return thunkAPI.rejectWithValue('Failed to fetch character deaths');
  }
});

// Async thunk to fetch "Voices" count for multiple characters
export const fetchBulkVoicesCount = createAsyncThunk<
  { [key: string]: number },
  { characterIds: string[]; game: string; league: string },
  { rejectValue: string }
>('characters/fetchBulkVoicesCount', async ({ characterIds, game, league }, thunkAPI) => {
  try {
    const voices = await getBulkCharacterVoices(characterIds, game, league);
    return voices;
  } catch (error: any) {
    return thunkAPI.rejectWithValue('Failed to fetch "Voices" count');
  }
});

// Async thunk to fetch alive statuses for multiple characters
export const fetchBulkIsAlive = createAsyncThunk<{ [key: string]: string | null }, { characterIds: string[] }, { rejectValue: string }>(
  'characters/fetchBulkIsAlive',
  async ({ characterIds }, thunkAPI) => {
    try {
      const aliveStatuses = await getBulkIsAlive(characterIds);
      return aliveStatuses;
    } catch (error: any) {
      return thunkAPI.rejectWithValue('Failed to fetch alive statuses');
    }
  },
);

// Async thunk to fetch last inventory update timestamps for multiple characters
export const fetchBulkLastInventoryUpdate = createAsyncThunk<
  { [key: string]: string | null },
  { characterIds: string[] },
  { rejectValue: string }
>('characters/fetchBulkLastInventoryUpdate', async ({ characterIds }, thunkAPI) => {
  try {
    const inventoryTimestamps = await getBulkLastInventoryUpdate(characterIds);
    return inventoryTimestamps;
  } catch (error: any) {
    return thunkAPI.rejectWithValue('Failed to fetch last inventory update timestamps');
  }
});

export const deleteCharacter = createAsyncThunk<string, string, { rejectValue: string }>(
  'characters/deleteCharacter',
  async (characterId, thunkAPI) => {
    try {
      await deleteCharacterService(characterId);
      return characterId;
    } catch (error: any) {
      let errorMessage = 'Failed to delete character';
      if (error.response && error.response.data && error.response.data.message) {
        if (Array.isArray(error.response.data.message)) {
          errorMessage = error.response.data.message.join(', ');
        } else {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  },
);

export const bulkDeleteCharacters = createAsyncThunk<
  { deletedCharacterIds: string[]; deletedCount: number; errors: string[] },
  string[],
  { rejectValue: string }
>('characters/bulkDeleteCharacters', async (characterIds, thunkAPI) => {
  try {
    if (characterIds.length === 0) {
      return thunkAPI.rejectWithValue('No characters selected for deletion');
    }

    const result = await bulkDeleteCharactersService(characterIds);

    // Create a more sophisticated approach to determine deleted characters
    // Since the backend doesn't return which specific characters were deleted,
    // we'll need to refetch the characters to see which ones are gone
    // For now, we'll use a heuristic: if there are no errors, assume all were deleted
    // If there are errors, assume the first deletedCount characters were successful
    let deletedCharacterIds: string[];

    if (result.errors.length === 0) {
      // No errors, all characters should be deleted
      deletedCharacterIds = characterIds;
    } else {
      // Some errors occurred, assume first deletedCount were successful
      deletedCharacterIds = characterIds.slice(0, result.deletedCount);
    }

    return {
      deletedCharacterIds,
      deletedCount: result.deletedCount,
      errors: result.errors,
    };
  } catch (error: any) {
    let errorMessage = 'Failed to bulk delete characters';
    if (error.response && error.response.data && error.response.data.message) {
      if (Array.isArray(error.response.data.message)) {
        errorMessage = error.response.data.message.join(', ');
      } else {
        errorMessage = error.response.data.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.response && error.response.status) {
      switch (error.response.status) {
        case 401:
          errorMessage = 'You are not authorized to delete characters';
          break;
        case 403:
          errorMessage = 'You do not have permission to delete these characters';
          break;
        case 404:
          errorMessage = 'Some characters were not found';
          break;
        case 500:
          errorMessage = 'Server error occurred while deleting characters';
          break;
        default:
          errorMessage = `HTTP ${error.response.status}: Failed to delete characters`;
      }
    }
    return thunkAPI.rejectWithValue(errorMessage);
  }
});

const charactersSlice = createSlice({
  name: 'characters',
  initialState,
  reducers: {
    clearCharactersError(state) {
      state.error = null;
      state.status = 'idle';
    },
    // Add other reducers if needed, e.g., for adding/updating/deleting characters locally
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCharacters.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCharacters.fulfilled, (state, action: PayloadAction<Character[]>) => {
        state.status = 'succeeded';
        state.characters = action.payload;
      })
      .addCase(fetchCharacters.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload; // Payload is rejectValue (string)
      })
      .addCase(fetchCharacterDeaths.fulfilled, (state, action) => {
        const deathCounts = action.payload;
        state.characters.forEach(character => {
          if (deathCounts[character.id] !== undefined) {
            character.deathsCount = deathCounts[character.id];
            character.deathsPerHour = deathCounts[character.id] / 4; // Assuming 4 hours
          }
        });
      })
      .addCase(fetchBulkVoicesCount.fulfilled, (state, action) => {
        const voicesCounts = action.payload;
        state.characters.forEach(character => {
          if (voicesCounts[character.id] !== undefined) {
            character.voicesCount = voicesCounts[character.id];
          }
        });
      })
      .addCase(fetchBulkIsAlive.pending, state => {
        state.aliveStatus = 'loading';
      })
      .addCase(fetchBulkIsAlive.fulfilled, (state, action) => {
        state.aliveStatus = 'succeeded';
        state.aliveStatuses = action.payload;
      })
      .addCase(fetchBulkIsAlive.rejected, (state, _action) => {
        state.aliveStatus = 'failed';
      })
      .addCase(fetchBulkLastInventoryUpdate.pending, state => {
        state.lastInventoryUpdateStatus = 'loading';
      })
      .addCase(fetchBulkLastInventoryUpdate.fulfilled, (state, action) => {
        state.lastInventoryUpdateStatus = 'succeeded';
        state.lastInventoryUpdateTimestamps = action.payload;
      })
      .addCase(fetchBulkLastInventoryUpdate.rejected, (state, _action) => {
        state.lastInventoryUpdateStatus = 'failed';
      })
      .addCase(deleteCharacter.fulfilled, (state, action: PayloadAction<string>) => {
        const deletedCharacterId = action.payload;
        state.characters = state.characters.filter(c => c.id !== deletedCharacterId);
      })
      .addCase(deleteCharacter.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(bulkDeleteCharacters.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(bulkDeleteCharacters.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { deletedCharacterIds } = action.payload;
        state.characters = state.characters.filter(c => !deletedCharacterIds.includes(c.id));
      })
      .addCase(bulkDeleteCharacters.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { clearCharactersError } = charactersSlice.actions;

// Selectors
export const selectAllCharacters = (state: RootState) => state.characters.characters;
export const selectCharactersStatus = (state: RootState) => state.characters.status;
export const selectCharactersError = (state: RootState) => state.characters.error;
export const selectAliveStatuses = (state: RootState) => state.characters.aliveStatuses;
export const selectLastInventoryUpdateTimestamps = (state: RootState) => state.characters.lastInventoryUpdateTimestamps;
export const selectLastInventoryUpdateStatus = (state: RootState) => state.characters.lastInventoryUpdateStatus;
export const selectAliveStatus = (state: RootState) => state.characters.aliveStatus;

export default charactersSlice.reducer;
